import React, { useEffect, useState } from "react";
import { PostApiMethod } from "../../core/services/apiService";
import "./AgentAttendanceTimeline.css";

export interface TimelineSegment {
    status: string;
    start_time: string;
    end_time: string;
    duration_ms: number;
}

export interface AggDay {
    date: string;
    online_ms: number;
    busy_ms: number;
    away_ms: number;
    collections_ms: number;
    offline_ms: number;
    total_worked_ms: number;
    first_activity_at: string;
    last_activity_at: string;
}

export interface AgentTimelineData {
    segments: TimelineSegment[];
    agg_days?: AggDay[];
    totals: {
        online_ms: number;
        busy_ms: number;
        away_ms: number;
        collections_ms: number;
        offline_ms: number;
        online_formatted: string;
        busy_formatted: string;
        away_formatted: string;
        collections_formatted: string;
        total_worked_ms: number;
        total_worked_formatted: string;
    };
    first_activity: string;
    last_activity: string;
}

interface Props {
    agentId: string;
    liveStatus: string;
    agentName?: string;
}

const statusColors: Record<string, string> = {
    online: "#439a43",
    busy: "#0187C4",
    away: "#ED8B00",
    collections: "#9C27B0",
    offline: "#c5c9ce",
};

const formatDuration = (ms: number): string => {
    const safeMs = Math.max(0, ms || 0);
    const totalSec = Math.floor(safeMs / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h > 0) {
        return `${h}h:${m}m:${s}s`;
    }
    return `${m}m:${s}s`;
};

// Reusable Intl.DateTimeFormat instances — per MDN recommendation:
// "When formatting large numbers of dates, it is better to create an
// Intl.DateTimeFormat object and use its format() method."
// Each toLocaleTimeString/toLocaleDateString call internally creates and destroys
// a formatter. Reusing one is 10-50x faster for bulk operations.
const reusableDateFmt = new Intl.DateTimeFormat("en-US");
const reusableTimeFmt = new Intl.DateTimeFormat(undefined, {
    hour: "2-digit", minute: "2-digit", hour12: true
});
const reusableTimeSecFmt = new Intl.DateTimeFormat(undefined, {
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true
});

const formatTime = (dateStr: string | undefined | null): string => {
    if (!dateStr) return "--:--";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "--:--";
    return reusableTimeFmt.format(d);
};

const formatDateOnly = (dateStr: string): string => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    return reusableDateFmt.format(d);
};

const formatTimeOnly = (dateStr: string): string => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    return reusableTimeSecFmt.format(d);
};

/**
 * Processes segments for CSV export per day.
 * Each segment uses its actual start_time and end_time from the database.
 * Groups by start_time date, per day starts from first active entry.
 * Duration for each segment = difference between its start_time and end_time.
 * For the last segment of a day, the end_time is capped to the last activity's
 * end_time on that same day (no stretching into next day).
 */
const processSegmentsForCSV = (segments: TimelineSegment[]): TimelineSegment[] => {
    // Group segments by date (using start_time)
    const daySegments: { [date: string]: TimelineSegment[] } = {};
    for (const seg of segments) {
        const date = formatDateOnly(seg.start_time);
        if (!daySegments[date]) daySegments[date] = [];
        daySegments[date].push(seg);
    }

    const result: TimelineSegment[] = [];
    for (const date of Object.keys(daySegments).sort((a, b) => new Date(a).getTime() - new Date(b).getTime())) {
        const segs = daySegments[date];

        // Find first active (non-offline) segment of the day
        const firstActiveIdx = segs.findIndex(s => s.status !== "offline");
        if (firstActiveIdx < 0) continue;

        // Keep entries from first active, drop last offline (duty completed)
        let dayEntries = segs.slice(firstActiveIdx);
        while (dayEntries.length > 0 && dayEntries[dayEntries.length - 1].status === "offline") {
            dayEntries.pop();
        }
        if (dayEntries.length === 0) continue;

        for (let i = 0; i < dayEntries.length; i++) {
            const seg = dayEntries[i];
            const segStartMs = new Date(seg.start_time).getTime();
            const segEndMs = new Date(seg.end_time).getTime();

            // Check if end_time falls on a different date than start_time
            // by comparing the formatted date strings (reuses cached Intl formatter)
            if (formatDateOnly(seg.end_time) !== date) {
                // Segment crossing midnight: cap to next segment's start or end of day
                const nextSeg = dayEntries[i + 1];
                if (nextSeg) {
                    const nextStartMs = new Date(nextSeg.start_time).getTime();
                    result.push({
                        status: seg.status,
                        start_time: seg.start_time,
                        end_time: nextSeg.start_time,
                        duration_ms: nextStartMs - segStartMs,
                    });
                } else {
                    // Last segment of the day crosses midnight — cap to end of day
                    const endOfDay = new Date(segStartMs);
                    endOfDay?.setHours(23, 59, 59, 999);
                    const endOfDayMs = endOfDay.getTime();
                    result.push({
                        status: seg.status,
                        start_time: seg.start_time,
                        end_time: endOfDay.toISOString(),
                        duration_ms: endOfDayMs - segStartMs,
                    });
                }
            } else {
                result.push({
                    status: seg.status,
                    start_time: seg.start_time,
                    end_time: seg.end_time,
                    duration_ms: segEndMs - segStartMs,
                });
            }
        }
    }

    return result;
};

// Escape a value for safe CSV embedding: doubles any internal quotes,
// wraps in quotes, and strips leading formula characters (=, +, -, @)
// to prevent CSV injection in Excel/Sheets.
export const csvSafe = (val: string): string => {
    let s = (val || "").replace(/"/g, '""');
    if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
    return `"${s}"`;
};

export const buildTimelineCSVRows = (agentName: string, agentData: AgentTimelineData): string[][] => {
    const rows: string[][] = [];
    const { segments } = agentData;

    // Drop leading offline segments (before first login)
    const allSegments = segments || [];
    const firstActiveIdx = allSegments.findIndex(s => s.status !== "offline");
    const trimmedSegments = firstActiveIdx >= 0 ? allSegments.slice(firstActiveIdx) : [];

    // Process segments: group by day, use actual times
    const finalSegments = trimmedSegments.length > 0 ? processSegmentsForCSV(trimmedSegments) : [];

    // Also handle agg_days (daily summaries for data older than 30 days)
    const aggDays = agentData.agg_days || [];

    if (finalSegments.length === 0 && aggDays.length === 0) return rows;

    // Calculate Hrs Worked per day (Online + Busy only, exclude Away and Offline)
    const dailyWorkedMs: { [date: string]: number } = {};
    for (const seg of finalSegments) {
        const date = formatDateOnly(seg.start_time);
        if (seg.status === "online" || seg.status === "busy") {
            dailyWorkedMs[date] = (dailyWorkedMs[date] || 0) + (seg.duration_ms || 0);
        }
    }

    let isFirstRow = true;

    // Render aggregated daily summary rows first (older data)
    // Each day shows separate rows per status (Online, Busy, Away, Offline)
    const sortedAggDays = [...aggDays].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    for (const day of sortedAggDays) {
        const d = new Date(day.date + "T00:00:00");
        const dateStr = reusableDateFmt.format(d);
        const hrsWorked = formatDuration((day.online_ms || 0) + (day.busy_ms || 0));
        const firstTime = day.first_activity_at ? formatTimeOnly(day.first_activity_at) : "-";
        const lastTime = day.last_activity_at ? formatTimeOnly(day.last_activity_at) : "-";

        const statuses: { label: string; ms: number }[] = [
            { label: "Online", ms: day.online_ms || 0 },
            { label: "Busy", ms: day.busy_ms || 0 },
            { label: "Away", ms: day.away_ms || 0 },
            { label: "Collections", ms: day.collections_ms || 0 },
            { label: "Offline", ms: day.offline_ms || 0 },
        ];

        let isFirstStatusRow = true;
        for (const st of statuses) {
            if (st.ms <= 0) continue;
            rows.push([
                isFirstRow ? csvSafe(agentName) : "",
                st.label,
                dateStr,
                isFirstStatusRow ? firstTime : "",
                isFirstStatusRow ? lastTime : "",
                formatDuration(st.ms),
                isFirstStatusRow ? hrsWorked : "",
            ]);
            isFirstRow = false;
            isFirstStatusRow = false;
        }

        // If no status had any time (shouldn't happen, but safety)
        if (isFirstStatusRow) {
            rows.push([
                isFirstRow ? csvSafe(agentName) : "",
                "No activity",
                dateStr,
                firstTime,
                lastTime,
                "0m:0s",
                "0m:0s",
            ]);
            isFirstRow = false;
        }
    }

    // Track whether we've already printed Hrs Worked for each day
    const dayHrsPrinted: { [date: string]: boolean } = {};

    // Render fine-grained segment rows (recent data)
    finalSegments.forEach((seg) => {
        const status = seg?.status || "unknown";
        const date = formatDateOnly(seg?.start_time);
        const showHrsWorked = !dayHrsPrinted[date] && dailyWorkedMs[date] !== undefined;

        rows.push([
            isFirstRow ? csvSafe(agentName) : "",
            status?.charAt(0).toUpperCase() + status?.slice(1),
            date,
            formatTimeOnly(seg?.start_time),
            formatTimeOnly(seg?.end_time),
            formatDuration(seg?.duration_ms || 0),
            showHrsWorked ? formatDuration(dailyWorkedMs[date]) : "",
        ]);

        if (showHrsWorked) {
            dayHrsPrinted[date] = true;
        }
        isFirstRow = false;
    });

    return rows;
};

export const exportAgentTimelineCSV = (agentName: string, data: AgentTimelineData) => {
    if (!data || ((!data.segments || data.segments.length === 0) && (!data.agg_days || data.agg_days.length === 0))) return;

    const rows: string[][] = [];
    rows.push(["Agent Name", "Status", "Date", "Start Time", "End Time", "Duration", "Hrs Worked"]);
    rows.push(...buildTimelineCSVRows(agentName, data));

    if (rows.length <= 1) return; // only header, no data rows after filtering

    const csv = rows.map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `${agentName}-Timeline-${new Date().toLocaleDateString("en-CA")}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const AgentAttendanceTimeline: React.FC<Props> = ({ agentId, liveStatus, agentName }) => {
    const [data, setData] = useState<AgentTimelineData | null>(null);
    const [loading, setLoading] = useState(true);

    const isActive = liveStatus !== "offline";

    useEffect(() => {
        let isMounted = true;
        const fetchData = async () => {
            try {
                const today = new Date().toLocaleDateString("en-CA");
                const res = await PostApiMethod("admin/dashboard-chart/agent_activity_reports", {
                    from_date: today,
                    to_date: today,
                    clientTZ: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    include_segments: true,
                    agent_id: agentId
                });
                if (isMounted && res.data?.status === "Success" && res.data.data) {
                    const agentData = res.data.data[agentId];
                    if (agentData) {
                        setData(agentData);
                    }
                }
            } catch (err) {
                console.error("Failed to fetch activity data:", err);
            } finally {
                if (isMounted) setLoading(false);
            }
        };
        fetchData();
        return () => { isMounted = false; };
    }, [agentId]);

    if (loading) {
        return (
            <div className="attendance-timeline-container">
                <div className="attendance-timeline-loading">Loading activity data...</div>
            </div>
        );
    }

    if (!data || !data.segments || data.segments.length === 0) {
        return (
            <div className="attendance-timeline-container">
                <div className="attendance-timeline-empty">No activity recorded today</div>
            </div>
        );
    }

    const { segments, totals } = data;

    // Drop leading offline segments — bar starts from when agent first came online
    const firstActiveIdx = segments.findIndex(s => s.status !== "offline");
    const visibleSegments = firstActiveIdx >= 0 ? segments.slice(firstActiveIdx) : [];

    // If all segments were leading offline, show empty state
    if (visibleSegments.length === 0) {
        return (
            <div className="attendance-timeline-container">
                <div className="attendance-timeline-empty">No activity recorded today</div>
            </div>
        );
    }

    // Total duration of all visible segments (first login to last segment end)
    const totalVisibleMs = visibleSegments.reduce((sum, s) => sum + (s.duration_ms || 0), 0);

    // Compute offline duration only from visible segments (excludes leading offline before first activity)
    const visibleOfflineMs = visibleSegments
        .filter(s => s.status === "offline")
        .reduce((sum, s) => sum + (s.duration_ms || 0), 0);
    const offlineFormatted = formatDuration(visibleOfflineMs);

    // First login time and current/last activity time
    const firstSegment = visibleSegments[0];
    const lastSegment = visibleSegments[visibleSegments.length - 1];
    const firstLoginTime = formatTime(firstSegment.start_time);
    const endTime = isActive ? formatTime(new Date().toISOString()) : formatTime(lastSegment.end_time);

    // Bar scale: first login to 11:59 PM — leaves a natural gap at the end
    const firstLoginDate = new Date(firstSegment.start_time);
    const endOfDay = new Date(firstLoginDate);
    endOfDay.setHours(23, 59, 0, 0);
    const totalBarMs = endOfDay.getTime() - firstLoginDate.getTime();
    const fillPct = totalBarMs > 0 ? Math.min((totalVisibleMs / totalBarMs) * 100, 100) : 100;

    // Safe totals access
    const onlineMs = totals?.online_ms || 0;
    const busyMs = totals?.busy_ms || 0;

    return (
        <div className="attendance-timeline-container">
            {/* Stats row — centered with labels */}
            <div className="attendance-stats-row">
                <div className="attendance-stat-item">
                    <span className="attendance-stat-dot" style={{ backgroundColor: statusColors.online }}></span>
                    <span>Online: {totals?.online_formatted || "0m:0s"}</span>
                </div>
                <div className="attendance-stat-item">
                    <span className="attendance-stat-dot" style={{ backgroundColor: statusColors.busy }}></span>
                    <span>Busy: {totals?.busy_formatted || "0m:0s"}</span>
                </div>
                <div className="attendance-stat-item">
                    <span className="attendance-stat-dot" style={{ backgroundColor: statusColors.away }}></span>
                    <span>Away: {totals?.away_formatted || "0m:0s"}</span>
                </div>
                <div className="attendance-stat-item">
                    <span className="attendance-stat-dot" style={{ backgroundColor: statusColors.collections }}></span>
                    <span>Collections: {totals?.collections_formatted || "0m:0s"}</span>
                </div>
                <div className="attendance-stat-item">
                    <span className="attendance-stat-dot" style={{ backgroundColor: statusColors.offline }}></span>
                    <span>Offline: {offlineFormatted}</span>
                </div>
                <div className="attendance-stats-total">
                    {isActive && <span className="attendance-live-dot"></span>}
                    {formatDuration(onlineMs + busyMs)} Hrs worked
                    <button
                        className="attendance-export-btn"
                        title={`Export ${agentName || "Agent"} timeline`}
                        onClick={(e) => {
                            e.stopPropagation();
                            exportAgentTimelineCSV(agentName || "Agent", data);
                        }}
                    >
                        <i className="bi bi-download"></i>
                    </button>
                </div>
            </div>

            {/* Chronological bar: first login → live/last time */}
            <div className="attendance-bar-row">
                <span className="attendance-bar-time">{firstLoginTime}</span>
                <div className="attendance-shift-bar">
                    {visibleSegments.map((seg, idx) => {
                        if (!seg) return null;

                        const segWidthPct = totalVisibleMs > 0
                            ? ((seg.duration_ms || 0) / totalVisibleMs) * fillPct
                            : 0;

                        const isLastSegment = idx === visibleSegments.length - 1;
                        const isSegmentActive = isActive && isLastSegment && seg.status === liveStatus;
                        const statusLabel = seg.status
                            ? seg.status.charAt(0).toUpperCase() + seg.status.slice(1)
                            : "Unknown";

                        return (
                            <div
                                key={idx}
                                className={`attendance-shift-segment${isSegmentActive ? " segment-active" : ""}`}
                                style={{
                                    width: `${segWidthPct}%`,
                                    backgroundColor: statusColors[seg.status] || "#e0e0e0"
                                }}
                                title={`${statusLabel}: ${formatTime(seg.start_time)} - ${formatTime(seg.end_time)}`}
                            />
                        );
                    })}
                </div>
                <span className="attendance-bar-time">{endTime}</span>
            </div>
        </div>
    );
};

export default AgentAttendanceTimeline;
